/**
 * MetricCard — Reusable stat card with icon, label, value, and trend indicator.
 */
import type { ReactNode } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";

export type TrendDirection = "up" | "down" | "neutral";

interface MetricCardProps {
  icon: ReactNode;
  label: string;
  value: string | number;
  trend?: TrendDirection;
  trendLabel?: string;
  className?: string;
}

const trendConfig: Record<
  TrendDirection,
  { icon: typeof TrendingUp; color: string }
> = {
  up: { icon: TrendingUp, color: "text-success" },
  down: { icon: TrendingDown, color: "text-danger" },
  neutral: { icon: Minus, color: "text-muted-foreground" },
};

export function MetricCard({
  icon,
  label,
  value,
  trend,
  trendLabel,
  className,
}: MetricCardProps) {
  return (
    <Card className={cn("p-5", className)} data-testid="metric-card">
      <div className="flex items-start justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </div>
        {trend && (
          <div className={cn("flex items-center gap-1", trendConfig[trend].color)}>
            {(() => {
              const TrendIcon = trendConfig[trend].icon;
              return <TrendIcon className="h-4 w-4" />;
            })()}
            {trendLabel && (
              <span className="text-xs font-medium">{trendLabel}</span>
            )}
          </div>
        )}
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </Card>
  );
}
